/**
 * web-studio-sites D7 — `Telify.draw('<piece>', (state, actions) => markup)`:
 * a site's own drawing for a placeable piece that is not closed. The kit takes
 * over the stub's queue, so drawings registered before it loaded apply too.
 * Replacement is refused, with a console error, for the six standard-only
 * pieces (the checkout and its five steps) and every piece a page cannot
 * place; those always show as standard.
 */
import type { TelifyStubGlobal } from './global';
import { KIT_PIECES } from './pieces';

/** A site's drawing: the piece's state and its checked actions in, markup out. */
export type Drawing = (state: unknown, actions: unknown) => unknown;

/** The page's drawings, by piece. */
export class Drawings {
  private readonly byPiece = new Map<string, Drawing>();
  private readonly listeners = new Set<() => void>();

  readonly draw = (piece: string, drawing: unknown): void => {
    const contract = KIT_PIECES.get(piece)?.contract;
    if (!contract?.placeable || contract.drawing === 'standard-only') {
      console.error(`[telify] Telify.draw("${piece}"): this piece cannot be redrawn; it shows as standard.`);
      return;
    }
    if (typeof drawing !== 'function') {
      console.error(`[telify] Telify.draw("${piece}"): the drawing must be a function (state, actions) => markup.`);
      return;
    }
    this.byPiece.set(piece, drawing as Drawing);
    for (const listener of this.listeners) listener();
  };

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly get = (piece: string): Drawing | undefined => this.byPiece.get(piece);

  /** Becomes `window.Telify.draw`, after applying what the stub queued. */
  install(): void {
    const w = window as unknown as { Telify?: TelifyStubGlobal };
    const queued = w.Telify?.queue ?? [];
    w.Telify = { draw: this.draw };
    for (const [piece, drawing] of queued) this.draw(piece, drawing);
  }
}
